import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price: number;
  popular: boolean;
  savings: string;
}

const creditPackages: CreditPackage[] = [
  { id: 'quick-5k', name: '5 mil créditos', credits: 5000, price: 35, popular: false, savings: '' },
  { id: 'quick-10k', name: '10 mil créditos', credits: 10000, price: 60, popular: true, savings: 'Economize 15%' },
  { id: 'quick-25k', name: '25 mil créditos', credits: 25000, price: 130, popular: false, savings: 'Economize 25%' },
  { id: 'quick-50k', name: '50 mil créditos', credits: 50000, price: 250, popular: false, savings: 'Economize 30%' },
  { id: 'quick-100k', name: '100 mil créditos', credits: 100000, price: 450, popular: false, savings: 'Economize 40%' },
];

export default function CreditLandingPage() {
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleBuy = async (pkg: CreditPackage) => {
    setLoading(pkg.id);
    setError('');
    setSuccess('');

    try {
      const base = __BASE_PATH__;
      const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base;
      const successUrl = window.location.origin + cleanBase + '/creditos?status=success';
      const failureUrl = window.location.origin + cleanBase + '/creditos?status=failure';
      const pendingUrl = window.location.origin + cleanBase + '/creditos?status=pending';

      const { data, error: invokeError } = await supabase.functions.invoke(
        'create-mercadopago-preference',
        {
          body: {
            planType: 'credits_standalone',
            credits: pkg.credits,
            price: pkg.price,
            successUrl,
            failureUrl,
            pendingUrl,
          },
        }
      );

      if (invokeError) {
        setError('Erro ao criar pagamento. Tente novamente.');
        setLoading(null);
        return;
      }

      if (data?.initPoint) {
        window.location.href = data.initPoint;
      } else {
        setError('Erro ao gerar link de pagamento.');
        setLoading(null);
      }
    } catch (err) {
      setError('Erro inesperado.');
      setLoading(null);
    }
  };

  // Handle return status
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    if (status === 'success') {
      setSuccess('Pagamento aprovado! Seus créditos foram adicionados.');
      window.history.replaceState({}, '', '/creditos');
    } else if (status === 'failure') {
      setError('Pagamento não aprovado. Tente novamente.');
      window.history.replaceState({}, '', '/creditos');
    } else if (status === 'pending') {
      setSuccess('Pagamento pendente. Seus créditos serão liberados em breve.');
      window.history.replaceState({}, '', '/creditos');
    }
  }, []);

  return (
    <div className="min-h-screen bg-background-50 dark:bg-background-100">
      {/* Schema.org Product JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Créditos de Envio WZap PRO",
            "description": "Créditos de envio para mensagens via infraestrutura WZap. Compre apenas o que precisa, sem assinatura, sem compromisso.",
            "brand": {
              "@type": "Brand",
              "name": "WZap PRO"
            },
            "offers": {
              "@type": "AggregateOffer",
              "priceCurrency": "BRL",
              "lowPrice": "35.00",
              "highPrice": "450.00",
              "offerCount": "5"
            }
          })
        }}
      />
      {/* Nav */}
      <nav className="bg-white/80 dark:bg-background-100/80 backdrop-blur-md border-b border-background-200/60 dark:border-background-300/30">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-[64px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
              <i className="ri-whatsapp-fill text-white text-sm" />
            </div>
            <span className="font-heading font-bold text-foreground-950 dark:text-foreground-100 text-base">WZap PRO</span>
          </Link>
          <Link
            to="/login"
            className="text-sm font-medium bg-primary-500 hover:bg-primary-600 text-white px-5 py-2 rounded-xl transition-all duration-200 whitespace-nowrap"
          >
            Entrar
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-5 md:px-8 py-12 md:py-20">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-50 dark:bg-accent-500/10 border border-accent-200 dark:border-accent-500/20 mb-6">
            <i className="ri-coin-line text-accent-600 dark:text-accent-400" />
            <span className="text-xs font-medium text-accent-700 dark:text-accent-300">Checkout Rápido</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-heading font-extrabold text-foreground-950 dark:text-foreground-100 mb-4">
            Compre Créditos de Envio
          </h1>
          <p className="text-base text-foreground-500 dark:text-foreground-400 max-w-xl mx-auto">
            Recarregue créditos para enviar mensagens via infraestrutura WZap.
            Sem compromisso, sem assinatura. Compre apenas o que precisa.
          </p>
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl px-4 py-3 flex items-center gap-3 mb-6 max-w-lg mx-auto">
            <i className="ri-error-warning-line text-red-500 text-lg flex-shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}
        {success && (
          <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-xl px-4 py-3 flex items-center gap-3 mb-6 max-w-lg mx-auto">
            <i className="ri-check-line text-emerald-500 text-lg flex-shrink-0" />
            <p className="text-sm text-emerald-700 dark:text-emerald-400">{success}</p>
          </div>
        )}

        {/* Packages */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
          {creditPackages.map((pkg) => {
            const isLoading = loading === pkg.id;
            return (
              <div
                key={pkg.id}
                className={`relative bg-white dark:bg-background-100 rounded-2xl border p-6 text-center transition-all duration-300 ${
                  pkg.popular
                    ? 'border-accent-500 ring-2 ring-accent-500/10 shadow-sm'
                    : 'border-background-200 dark:border-background-300 hover:border-background-300 dark:hover:border-background-400'
                }`}
              >
                {pkg.popular && (
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-accent-500 text-white text-[10px] font-bold px-3 py-1 rounded-full whitespace-nowrap">
                    Mais Popular
                  </span>
                )}
                <div className="w-14 h-14 rounded-2xl bg-accent-50 dark:bg-accent-500/10 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-stack-line text-2xl text-accent-600 dark:text-accent-400" />
                </div>
                <p className="text-base font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-1">
                  {pkg.name}
                </p>
                <p className="text-3xl font-heading font-extrabold text-foreground-950 dark:text-foreground-100 mb-1">
                  R$ {pkg.price.toLocaleString('pt-BR')}
                </p>
                {pkg.savings && (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium mb-3">
                    {pkg.savings}
                  </p>
                )}
                <p className="text-xs text-foreground-500 mb-5">
                  {pkg.credits.toLocaleString('pt-BR')} envios
                </p>
                <button
                  onClick={() => handleBuy(pkg)}
                  disabled={isLoading}
                  className={`w-full text-sm font-semibold px-5 py-3 rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 ${
                    pkg.popular
                      ? 'bg-accent-500 hover:bg-accent-600 text-white shadow-sm'
                      : 'bg-background-50 dark:bg-background-200 text-foreground-700 dark:text-foreground-300 hover:bg-background-100 border border-background-200 dark:border-background-300'
                  }`}
                >
                  {isLoading ? (
                    <><div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />Redirecionando...</>
                  ) : (
                    <><i className="ri-shopping-cart-line" />Comprar Agora</>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
          <div className="bg-white dark:bg-background-100 rounded-xl border border-background-200 dark:border-background-300 p-4 text-center">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center mx-auto mb-3">
              <i className="ri-flashlight-line text-lg text-emerald-600 dark:text-emerald-400" />
            </div>
            <p className="text-xs font-semibold text-foreground-900 dark:text-foreground-100 mb-1">Entrega Imediata</p>
            <p className="text-[10px] text-foreground-500">Créditos liberados assim que o pagamento for aprovado</p>
          </div>
          <div className="bg-white dark:bg-background-100 rounded-xl border border-background-200 dark:border-background-300 p-4 text-center">
            <div className="w-10 h-10 rounded-lg bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center mx-auto mb-3">
              <i className="ri-shield-check-line text-lg text-primary-600 dark:text-primary-400" />
            </div>
            <p className="text-xs font-semibold text-foreground-900 dark:text-foreground-100 mb-1">Pagamento Seguro</p>
            <p className="text-[10px] text-foreground-500">Processado pelo MercadoPago com criptografia</p>
          </div>
          <div className="bg-white dark:bg-background-100 rounded-xl border border-background-200 dark:border-background-300 p-4 text-center">
            <div className="w-10 h-10 rounded-lg bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center mx-auto mb-3">
              <i className="ri-infinity-line text-lg text-amber-600 dark:text-amber-400" />
            </div>
            <p className="text-xs font-semibold text-foreground-900 dark:text-foreground-100 mb-1">Sem Expiração</p>
            <p className="text-[10px] text-foreground-500">Seus créditos nunca expiram. Use quando quiser</p>
          </div>
        </div>

        {/* CTA to full platform */}
        <div className="text-center mt-12 pt-8 border-t border-background-200 dark:border-background-300">
          <p className="text-sm text-foreground-500 mb-3">
            Quer acesso completo ao painel com campanhas, relatórios e CRM?
          </p>
          <Link
            to="/login"
            className="inline-flex bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-6 py-3 rounded-xl transition-all duration-200 whitespace-nowrap"
          >
            Acessar WZap PRO Completo
            <i className="ri-arrow-right-line ml-2" />
          </Link>
        </div>
      </div>
    </div>
  );
}